import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  NLSCustomer,
  NlsCustomerValidationService,
} from '@lmc-app/core/services/nls-customer-validation.service';

/**
 * Audits every NLS customer against the customer-info rules payment processing applies
 * before submitting a transaction to KapCharge, so gaps can be fixed in NLS ahead of an
 * upload rather than being discovered row by row in the payment preview.
 */
@Component({
  selector: 'app-nls-customer-validation',
  templateUrl: './nls-customer-validation.component.html',
  styleUrls: ['./nls-customer-validation.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class NlsCustomerValidationComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    'loanNumber',
    'customerName',
    'email',
    'phone',
    'address',
    'bankAccount',
  ];
  readonly pageSizeOptions = [50, 100];

  customers: NLSCustomer[] = [];
  rowCount = 0;
  validCount = 0;
  incompleteCount = 0;
  pageIndex = 0;
  pageSize = 50;
  isLoading = false;

  searchControl = new FormControl<string>('', { nonNullable: true });
  /**
   * Opens on the customers that need work — a complete record needs no attention here.
   * The filter stays available so the full book is still one click away.
   */
  statusFilter = new FormControl<string>('incomplete', { nonNullable: true });

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly nlsCustomerValidationService: NlsCustomerValidationService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadCustomers();

    this.searchControl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadCustomers();
      });

    this.statusFilter.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadCustomers();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCustomers(): void {
    this.isLoading = true;
    this.nlsCustomerValidationService
      .list(this.pageIndex + 1, this.pageSize, this.searchControl.value, this.statusFilter.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.customers = res.results;
          this.rowCount = res.rowCount;
          this.validCount = res.validCount;
          this.incompleteCount = res.incompleteCount;
          this.isLoading = false;
        },
        error: (err) => {
          const msg = err.error?.message || 'Failed to load customers';
          this.snackBar.open(msg, 'Close', { duration: 5000 });
          this.isLoading = false;
        },
      });
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadCustomers();
  }

  trackByAcctrefno(_index: number, customer: NLSCustomer): string {
    return customer.acctrefno;
  }

  /**
   * Customer-info gaps the Email and Phone cells cannot show on their own. Dropping the
   * summary column left these invisible: a joined address simply gets shorter when a part
   * is missing, and an absent name reads as a shorter name.
   */
  private issuesAmong(customer: NLSCustomer, fields: string[]): string[] {
    return customer.missingFields.filter((f) => fields.includes(f));
  }

  nameIssues(customer: NLSCustomer): string[] {
    return this.issuesAmong(customer, ['First Name', 'Last Name']);
  }

  addressIssues(customer: NLSCustomer): string[] {
    return this.issuesAmong(customer, ['Address', 'City', 'Province', 'Postal Code']);
  }

  /**
   * Faults on the bank side of this row, as chip labels. Empty means the account is fine
   * (or absent, which the cell states directly). The routing number is included on a branch
   * miss so it can be looked up or added to the FIF branch list without going back to NLS.
   */
  bankIssues(customer: NLSCustomer): string[] {
    if (!customer.hasBankAccount) return [];
    const issues = [...customer.bankMissingFields];
    if (customer.branchInCache === false) {
      issues.push(`Invalid routing number (${customer.dprn}) — not in FIF branch list`);
    }
    return issues;
  }

  /** "No customers found" reads like a failure when the empty set is the good outcome. */
  get emptyStateMessage(): string {
    if (this.statusFilter.value === 'incomplete' && !this.searchControl.value.trim()) {
      return 'No customers are missing information.';
    }
    return 'No customers found.';
  }

  formatAddress(customer: NLSCustomer): string {
    const parts = [customer.address, customer.city, customer.province, customer.postalCode]
      .filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  }
}
